import { ReactElement, ReactNode, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { Table, TableProps } from '../../../../components/table/index';
import { useBrowserPageReveal } from '../../hooks/use-browser-page-reveal.hook';
import { SpaceBrowserPagination } from '../../types/data-room.types';
import { chunksOf } from '../../utils/chunks.util';
import {
  BrowserLoadingRows,
  BrowserPagePanel,
  BrowserPaginationBar,
  PAGE_ROW_CLASS,
} from '../../workspaces/browser/workspace-browser-pagination';
import { ACTIVITY_PAGE_SIZE } from '../hooks/use-activity-pages.hook';
import { TABLE_PLACEHOLDER_ROWS } from './activity-loading-placeholders';

type ActivityPagedTableProps<Row, TColumn extends string> = {
  pagination: SpaceBrowserPagination;
  // A page past the first sits in a table of its own, so its rows take the divider as a class.
  renderRow: (row: Row, className?: string) => ReactNode;
  rows: Row[];
} & Pick<
  TableProps<TColumn, object>,
  'ariaSort' | 'className' | 'columnOrder' | 'columnsTitle' | 'tHeadClassNames'
>;

/**
 * An Activity list in its bordered card, paged as the Workspaces browser pages a folder: the first
 * page stays put, each later one opens in a panel under it, and the shared pill below loads the
 * next page or folds back to the first.
 */
export const ActivityPagedTable = <Row, TColumn extends string>({
  className,
  pagination,
  renderRow,
  rows,
  ...table
}: ActivityPagedTableProps<Row, TColumn>): ReactElement => {
  const cardRef = useRef<HTMLDivElement>(null);
  const { barRef, isFolding, onPagesFolded, showLess } = useBrowserPageReveal({
    pageSize: ACTIVITY_PAGE_SIZE,
    pagination,
    rowCount: rows.length,
    tableRef: cardRef,
  });
  const columnCount = table.columnOrder.length;
  const [firstPage = [], ...laterPages] = chunksOf(rows, ACTIVITY_PAGE_SIZE);

  return (
    // The card clips and the table scrolls inside it, so the pill below never scrolls sideways
    // with a wide table and folding still scrolls the tab, not the card.
    <div
      className="shrink-0 overflow-hidden rounded-md border border-gray-100 bg-white"
      ref={cardRef}
    >
      <div className="overflow-x-auto">
        <Table {...table} className={twMerge('table-fixed [&_tbody]:divide-gray-100', className)}>
          {firstPage.map((row) => renderRow(row))}
          {/* One list keyed by page, so the panel holding a page's placeholders is the very one
              its rows fill: it stays open and the rows fade in over them. */}
          {[
            ...laterPages.map((page, index) => (
              <BrowserPagePanel
                columnCount={columnCount}
                isFolding={isFolding}
                isLoading={false}
                key={`page-${index + 1}`}
                onFolded={index === 0 ? onPagesFolded : undefined}
              >
                {page.map((row) => renderRow(row, PAGE_ROW_CLASS))}
              </BrowserPagePanel>
            )),
            ...(pagination.isLoadingMore
              ? [
                  <BrowserPagePanel
                    columnCount={columnCount}
                    isFolding={false}
                    isLoading
                    key={`page-${laterPages.length + 1}`}
                  >
                    <BrowserLoadingRows
                      columnCount={columnCount}
                      count={Math.min(TABLE_PLACEHOLDER_ROWS, pagination.nextPageSize)}
                    />
                  </BrowserPagePanel>,
                ]
              : []),
          ]}
        </Table>
      </div>
      {(pagination.nextPageSize > 0 || pagination.canShowLess || isFolding) && (
        <BrowserPaginationBar
          count={pagination.nextPageSize}
          isLoading={pagination.isLoadingMore || pagination.isReloading}
          onLoadMore={pagination.loadMore}
          onShowLess={() => void showLess()}
          ref={barRef}
        />
      )}
    </div>
  );
};
